import React, { useRef, useState } from 'react';
import { IntFileItem } from '../models';
import { postAsset } from '../helpers/postAsset';
import classes from './MediaWidget.module.scss';

// The editors only accept horizontal JPGs of at least 1800x1020, see the
// instructions shown above the widget in App.tsx.
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/jpg'];
const MIN_WIDTH = 1800;
const MIN_HEIGHT = 1020;
const MAX_FILE_SIZE_BYTES = 10485760;

interface MediaWidgetProps {
  mediaArray: IntFileItem[];
  onChange: (value: IntFileItem[]) => void;
  readonly?: boolean;
}

const filenameFromUrl = (url: string) =>
  decodeURIComponent(url.split('?')[0].split('/').pop() || 'file');

const readImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const previewUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(previewUrl);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      reject(new Error('Could not read the image'));
    };
    img.src = previewUrl;
  });

const validateFile = async (file: File): Promise<string | null> => {
  if (!ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase())) {
    return 'Only JPG images can be uploaded.';
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'The image is too large. The maximum file size is 10 MB.';
  }

  let dimensions;
  try {
    dimensions = await readImageDimensions(file);
  } catch {
    return 'This file could not be read as an image.';
  }

  if (dimensions.width < dimensions.height) {
    return 'The image must be horizontal (landscape).';
  }
  if (dimensions.width < MIN_WIDTH || dimensions.height < MIN_HEIGHT) {
    return `The image is too small (${dimensions.width}x${dimensions.height}). It must be at least ${MIN_WIDTH}x${MIN_HEIGHT} pixels.`;
  }

  return null;
};

const MediaWidget: React.FC<MediaWidgetProps> = ({
  mediaArray,
  onChange,
  readonly,
}) => {
  const [newUrl, setNewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addItem = (item: IntFileItem) => {
    onChange([...mediaArray, item]);
  };

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    // Reset immediately so picking the same file twice still fires onChange.
    event.target.value = '';
    if (files.length === 0) {
      return;
    }

    setUploadError(null);

    for (const file of files) {
      const validationError = await validateFile(file);
      if (validationError) {
        setUploadError(validationError);
        return;
      }
    }

    setIsUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        uploaded.push(await postAsset(file));
      }
      onChange([
        ...mediaArray,
        ...uploaded.map((asset) => ({
          copyright: asset.copyright || null,
          filename: asset.fileName || filenameFromUrl(asset.uri),
          filetype: 'jpeg',
          hlink: asset.uri,
          main: null,
          mediatype: 'photo' as const,
          targetLanguage: null,
          title: null,
          trcid: null,
        })),
      ]);
    } catch (err) {
      setUploadError(
        'Uploading the image failed. Please try again, or add the image with a URL.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddUrl = () => {
    const url = newUrl.trim();
    if (!url) {
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      setUploadError('A media URL must start with http:// or https://');
      return;
    }
    setUploadError(null);
    addItem({
      copyright: null,
      filename: filenameFromUrl(url),
      filetype: 'jpeg',
      hlink: url,
      main: null,
      mediatype: 'photo',
      targetLanguage: null,
      title: null,
      trcid: null,
    });
    setNewUrl('');
  };

  const handleCopyrightChange = (index: number, copyright: string) => {
    onChange(
      mediaArray.map((item, i) =>
        i === index ? { ...item, copyright: copyright || null } : item
      )
    );
  };

  const handleRemove = (index: number) => {
    onChange(mediaArray.filter((_, i) => i !== index));
  };

  return (
    <div className={classes.mediaWidget}>
      {mediaArray.map((item, index) => (
        <div key={`${item.hlink}-${index}`} className={classes.mediaItem}>
          <img className={classes.thumbnail} src={item.hlink} alt={item.filename} />
          <div className={classes.mediaInfo}>
            <p>
              <a href={item.hlink} target="_blank" rel="noreferrer">
                {item.filename}
              </a>
            </p>
            <label className={classes.copyrightLabel} htmlFor={`copyright-${index}`}>
              Copyright *
            </label>
            <input
              id={`copyright-${index}`}
              type="text"
              className={[
                classes.copyrightInput,
                item.copyright && item.copyright.trim()
                  ? ''
                  : classes.missingCopyright,
              ].join(' ')}
              value={item.copyright || ''}
              onChange={(e) => handleCopyrightChange(index, e.target.value)}
              placeholder="Who owns this image?"
              disabled={readonly}
            />
          </div>
          {!readonly && (
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className={classes.removeButton}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {!readonly && (
        <>
          <div className={classes.addMediaSection}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg"
              multiple
              className={classes.hiddenFileInput}
              onChange={handleFilesSelected}
              data-testid="media-file-input"
            />
            <button
              type="button"
              className={classes.uploadButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Choose image'}
            </button>
            <span className={classes.separator}>or</span>
            <input
              type="text"
              className={classes.urlInput}
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="Enter media URL"
            />
            <button
              type="button"
              className={classes.addUrlButton}
              onClick={handleAddUrl}
            >
              Add Media
            </button>
          </div>
          {uploadError && <div className={classes.uploadError}>{uploadError}</div>}
        </>
      )}
    </div>
  );
};

export default MediaWidget;
